import React from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {FormControl, FormLabel, Link, styled} from '@mui/material';

export const RadioB = withStyles((theme) => ({
  root: {
    'padding': '0px 5px 0px 10px',
    '&$checked': {
      color: theme.palette.primary.main,
    },
  },
  checked: {
  },
}))(Radio);

const disabledStyles = makeStyles((theme) => ({
  root: {
    '& .MuiOutlinedInput-input.Mui-disabled': {
      backgroundColor: '#dadada',
      WebkitTextFillColor: '#000000',
    },
  },
}));

const formLabelStyles = makeStyles((theme) => ({
  root: {
    fontSize: '0.875em',
  },
}));

const makeHelperTextStyles = makeStyles((theme) => ({
  root: {
    color: '#FF0000',           // ヘルパーテキストの文字色
  },
}));

const errorOutLineStyles = makeStyles((theme) => ({
  root: {
    '& label': {
      color: '#FF0000',           // 通常時のタイトルの文字色
    },
    '& label.Mui-focused': {
      color: '#FF0000',           // 入力時のタイトルの文字色
    },
    '& .MuiInput-underline': {
      '&:before': {
        borderColor: '#FF0000',    // 通常時のボーダー色(アウトライン)
      },
      '&:hover:not(.Mui-disabled):before': {
        borderColor: '#FF0000',    // ホバー時のボーダー色(アウトライン)
      },
      '&:after': {
        borderColor: '#FF0000',    // 入力時のボーダー色(アウトライン)
      },
    },
  },
}));

/** MUIのTextFieldにカスタムCSSを適用したコンポーネント */
const CustomTextField = styled(TextField)((props) => ({
  // .MuiInputBase-readOnly クラスの付いた項目に付加するStyle
  '& .MuiInputBase-readOnly': {
    'backgroundColor': '#F0F0F0',
  },
}));

/**
 * 項目がRead Onlyであるかの判定。
 *
 * @param {object} custom 入力項目パラメータ
 * @return {boolean} Read Only項目の場合true
 */
function isReadOnly(custom) {
  const inputProps = custom.InputProps;
  return inputProps && inputProps.readOnly;
}

/**
 * Read Only項目に共通のパラメータ設定を行う。
 *
 * @param {object} custom 入力項目パラメータ
 * @param {object} input 入力情報
 */
function setReadOnlyProps(custom, input) {
  if (custom == null || !isReadOnly(custom)) {
    return;
  }

  // ラベルの位置を固定
  if (!custom.InputLabelProps) {
    custom['InputLabelProps'] = {};
  }
  custom.InputLabelProps.shrink = true;

  // Placeholderが未設定の場合は共通の値を設定
  if (!custom.placeholder) {
    custom['placeholder'] = '入力不可';
  }
}

/**
 * エラーテキスト下線のスタイル設定を行う
 *
 * @param {object} custom 入力項目パラメータ
 * @param {object} touched
 * @param {object} error
 * @param {object} warning
 */
function setErrorOutLineProps(custom, touched, error, warning) {
  const outlineStyle = errorOutLineStyles();
  if ((custom != null) &&
      ((touched && error) || warning)) {
    custom['className'] = clsx(custom.className, outlineStyle.root);
  }
}

/**
 * ヘルパーテキストのスタイル設定を行う
 *
 * @param {object} custom 入力項目パラメータ
 * @param {object} touched
 * @param {object} error
 * @param {object} warning
 */
function setFormHelperTextProps(custom, touched, error, warning) {
  const helperTextStyles = makeHelperTextStyles();
  if ((custom != null) &&
      ((touched && error) || warning)) {
    custom['FormHelperTextProps'] = {classes: {root: helperTextStyles.root}};
  }
}

/**
 * disabled項目に共通のパラメータ設定を行う。
 *
 * @param {object} custom 入力項目パラメータ
 * @param {object} input 入力情報
 */
function setDisabledProps(custom) {
  const classes = disabledStyles();
  if (custom == null || !custom.disabled) {
    return;
  }
  custom['className'] = clsx(custom.className, classes.root);
}

export const renderTextField = ({
  input,
  label,
  meta: {touched, error, valid, warning},
  ...custom
}) => {
  // ReadOnlyの場合、パラメータを自動設定
  setReadOnlyProps(custom, input);
  if (isReadOnly(custom)) {
    // 未入力の日付項目の場合、yyyy/mm/ddを表示させないため属性をtextに変更する
    if (!input.value && custom.type === 'date') {
      custom.type = 'text';
    }
  }

  // ヘルパーテキストのスタイル設定を行う
  setFormHelperTextProps(custom, touched, error, warning);
  setErrorOutLineProps(custom, touched, error, warning);
  setDisabledProps(custom);

  return (
    <CustomTextField
      variant="standard"
      label={label}
      helperText={(touched && error) || warning}
      error={touched && !valid}
      {...input}
      {...custom} />
  );
};

// Autocomplete プルダウン表示スタイル
const AUTOCOMPLETE_POPPER_PROPS = {
  sx: {
    // プルダウン項目を折り返さずに表示
    '& .MuiAutocomplete-listbox': {
      '& .MuiAutocomplete-option': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    },
  },
};

/**
 * Autocomplete Field のレンダリング
 *
 * @param {Object} props 描画パラメータ
 *      - input   入力項目 (自動設定)
 *      - meta    入力状態 (自動設定)
 *      - options 入力選択肢 {value: 値, label: 表示文字列} の配列
 *      - moreProps 自動補完コンポーネント Autocomplete の属性
 *      - custom  テキストフィールドの属性
 *                (label, variant, required, size, style, ...)
 * @return {JSX.Element} Autocomplete エレメント
 */
export const renderAutocomplete = ({
  input,
  meta: {touched, error, valid, warning},
  options,
  moreProps,
  ...custom
}) => {
  setReadOnlyProps(custom, input);
  setFormHelperTextProps(custom, touched, error, warning);
  setErrorOutLineProps(custom, touched, error, warning);
  setDisabledProps(custom);
  const defProps = {
    autoComplete: true,        // 一覧選択中に入力値を表示
    autoHighlight: true,       // 一覧選択で補完した値をハイライト
    handleHomeEndKeys: false,  // HOMEキー/ENDキーを文字操作に使用
    autoSelect: true,          // ENTERキーなしで選択
    slotProps: {
      popper: AUTOCOMPLETE_POPPER_PROPS,
    },
  };
  return (
    <Autocomplete
      options={options}
      value={input.value || null}
      getOptionLabel={(opt) => opt.label || ''}
      isOptionEqualToValue={(opt, val) => opt.value === val?.value}
      noOptionsText=''
      onChange={(ev, val) => input.onChange(val || null)}
      {...defProps}
      {...moreProps}
      renderInput={(params) => (
        <TextField
          error={touched && !valid}
          helperText={(touched && error) || warning}
          {...params}
          {...custom}
        />
      )}
    />
  );
};

export const renderLink = ({
  input,
  label,
  ...custom
}) => {
  const _classes = formLabelStyles();
  const _className = clsx(custom.className, _classes.root);
  return (
    <FormControl component="fieldset">
      <FormLabel className={_className}>{label}</FormLabel>
      <Link
        href="#"
        onClick={(e) => {
          e.preventDefault();
          custom.click(e);
        }}
        variant={'subtitle1'}
      >
        {input.value}
      </Link>
    </FormControl>
  );
};

export const renderSelect = ({
  input,
  label,
  meta: {touched, error, valid, warning},
  ...custom
}) => {
  let isSelect = true;
  // 初期値指定がある場合、初期値を自動設定
  if (custom.defaultValue) {
    input.value = input.value ? input.value : custom.defaultValue;
  }
  // ReadOnlyの場合、パラメータを自動設定
  setReadOnlyProps(custom, input);
  if (isReadOnly(custom)) {
    // 値が未選択の場合、テキストボックスとして描画しPlaceholderを有効にする
    if (!input.value) {
      isSelect = false;
    }
  }
  // ヘルパーテキストのスタイル設定を行う
  setFormHelperTextProps(custom, touched, error, warning);
  setErrorOutLineProps(custom, touched, error, warning);
  setDisabledProps(custom);


  return (
    <CustomTextField
      variant="standard"
      select={isSelect}
      label={label}
      helperText={(touched && error) || warning}
      error={touched && !valid}
      {...input}
      {...custom} />
  );
};

export const renderSelectMultiLine = ({
  input,
  label,
  meta: {touched, error, valid, warning},
  ...custom
}) => {
  // useStylesの呼び出し数を合わせるため
  setReadOnlyProps(null, null);
  // ヘルパーテキストのスタイル設定を行う
  setFormHelperTextProps(custom, touched, error, warning);
  setErrorOutLineProps(custom, touched, error, warning);
  return (
    <CustomTextField
      variant="standard"
      select={true}
      SelectProps={{
        multiple: true,
        value: (input.value ? input.value : [])}}
      label={label}
      helperText={(touched && error) || warning}
      error={touched && !valid}
      {...input}
      {...custom} />
  );
};

export const renderRadioGroup = ({
  input,
  label,
  meta: {touched, error},
  direction,
  defaultValue,
  ...rest
}) => {
  // useStylesの呼び出し数を合わせるため
  setReadOnlyProps(null, null);
  setFormHelperTextProps(null, null, null, null);
  setErrorOutLineProps(null, null, null, null);
  // defaultValueが指定されているかつvalueが未設定の場合のみ設定s
  if (defaultValue && input && !input.value) {
    input.value = defaultValue;
  }
  if (direction === 'horizontal') {
    // direction: 'horizontal'が指定されている場合のみ横並び
    return (
      <div> {label}
        <RadioGroup
          {...input}
          {...rest}
          row
        />
        {touched && error &&
            <Typography variant="caption" color="error">
              {error}
            </Typography>
        }
      </div>
    );
  } else {
    // デフォルトは縦並び
    return (
      <div> {label}
        <RadioGroup
          {...input}
          {...rest}
        />
        {touched && error &&
            <Typography variant="caption" color="error">
              {error}
            </Typography>
        }
      </div>
    );
  }
};

export const renderCheckbox = ({input, label, disabled, ...props}) => {
  // useStylesの呼び出し数を合わせるため
  setReadOnlyProps(null, null);
  setFormHelperTextProps(null, null, null, null);
  setErrorOutLineProps(null, null, null, null);
  if (input.value === undefined && input.defaultChecked) {
    return (
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={input.defaultChecked}
            onChange={input.onChange}
            color="primary"
            disabled={!!disabled}
            {...props}
          />
        }
        label = { label }
      />
    );
  }
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={Boolean(input.value)}
          onChange={input.onChange}
          color="primary"
          disabled={!!disabled}
          {...props}
        />
      }
      label = { label }
    />
  );
};
